import * as React from 'react';
import {
	ComboboxMultipleSelection,
	Options,
} from '@/components/ui/comboboxMultipleSelection';
import { useFilters } from '@/hooks';
import useDeviceWidth from '@/hooks/useDeviceWidth';
import { useQuery } from '@tanstack/react-query';
import { GET_ELIGIBLE_STATES, lawyerDirectoryService } from '@/api';
import { useRouter } from 'next/router';

export const StateFilter = () => {
	const FILTER_KEY = 'state';
	const router = useRouter();
	const { id: caseId } = router.query;
	const width = useDeviceWidth();
	const { setParamFilter, query, hasFilter } = useFilters();
	const [selected, setSelected] = React.useState<string[]>([]);
	const [isOpen, setIsOpen] = React.useState(false);
	const [searchTerm, setSearchTerm] = React.useState('');

	const { data: statesData } = useQuery({
		queryKey: [GET_ELIGIBLE_STATES, caseId],
		queryFn: () => lawyerDirectoryService.getEligibleStates(caseId as string),
		enabled: !!caseId,
	});

	const optionsDefined = React.useMemo(() => {
		const auxOptions: Options[] = (statesData?.data ?? []).map((item) => ({
			label: item.name,
			value: item.id.toString(),
		}));

		if (!searchTerm) return auxOptions;

		return auxOptions.filter((option) =>
			(option.label as string).toLowerCase().includes(searchTerm.toLowerCase())
		);
	}, [statesData, searchTerm]);

	const handleSelectFilters = (value?: string): void => {
		if (value) {
			const newSelected = selected.includes(value)
				? selected.filter((s) => s !== value)
				: [...selected, value];
			setParamFilter(FILTER_KEY, newSelected.join(','));
		}
	};

	const handleClearAll = (): void => {
		setParamFilter(FILTER_KEY, '');
		setSelected([]);
	};

	React.useEffect(() => {
		const queryParam = query[FILTER_KEY] as string;
		if (queryParam) {
			setSelected(queryParam.split(','));
		} else {
			setSelected([]);
		}
	}, [query]);

	const handleSearch = (value: string) => {
		setSearchTerm(value);
	};

	return (
		<ComboboxMultipleSelection
			open={isOpen}
			setOpen={(value) => {
				if (width > 640) setIsOpen(value);
			}}
			hasFilter={hasFilter(FILTER_KEY)}
			buttonContentDefault={true}
			values={selected}
			labelDropdown="State"
			filterOptions={optionsDefined}
			handleSelectFilters={handleSelectFilters}
			handleClearAll={handleClearAll}
			classNamePopoverContent="!min-w-[212px] !w-fit"
			classNameTrigger="h-[33px] !px-2 !w-[73px]"
			withSearch
			handleSearch={handleSearch}
		/>
	);
};
